import { FormikProps } from "formik"
import { RegistrationPanelValidationType } from "./RegistrationPanelValidationSchema"

type PaymentMethodValue = NonNullable<RegistrationPanelValidationType["paymentMethods"]>[number]
type PaymentMethodError = string | Record<string, unknown> | undefined

const SEPA_FIELDS: Array<"sepaAccountholder" | "iban" | "bank" | "bic"> = ["sepaAccountholder", "iban", "bank", "bic"]

/** Gespeicherte Methode, die in diesem Formular nicht angefasst wurde (gleiche ID, gleiche Lastschrift-Daten). */
const isUntouchedPersistedPaymentMethod = (paymentMethod: PaymentMethodValue | undefined, initialPaymentMethods: Array<PaymentMethodValue>): boolean => {
    const initial = initialPaymentMethods.find((pm) => pm.id === paymentMethod?.id)

    return initial !== undefined && SEPA_FIELDS.every((field) => (initial[field] ?? "") === (paymentMethod?.[field] ?? ""))
}

/**
 * Echte Yup-Fehler NEUER oder GEÄNDERTER Zahlungsmethoden blockieren das Speichern (B-82808:
 * eine 18-stellige IBAN kam durch, weil hier früher alle echten Fehler verworfen wurden).
 * Fehler unveränderter Bestandsmethoden bleiben außen vor: Die leere Platzhalter-Lastschrift
 * aus dem Checkout oder eine Alt-IBAN dürfen das Konto-Formular nicht sperren; sie werden über
 * den Bearbeiten-Link korrigiert. Array-weite String-Fehler (z. B. `required`) zählen wie bisher nicht.
 */
const sanatizePaymentMethodErrors = (
    allPaymentMethodErrors: unknown,
    paymentMethods: Array<PaymentMethodValue>,
    initialPaymentMethods: Array<PaymentMethodValue>,
): Array<PaymentMethodError> => {
    if (!Array.isArray(allPaymentMethodErrors)) return []
    const isRealError = (error: PaymentMethodError) => (typeof error === "string" ? error !== "" : Boolean(error) && Object.keys(error ?? {}).length > 0)

    return allPaymentMethodErrors.map((error: PaymentMethodError, index: number) =>
        isRealError(error) && !isUntouchedPersistedPaymentMethod(paymentMethods[index], initialPaymentMethods) ? error : undefined,
    )
}

export const sanatizeErrors = (formikProps: FormikProps<RegistrationPanelValidationType>) => {
    const errors = formikProps.errors
    const allPaymentMethodErrors = errors.paymentMethods ?? []
    const sanatizedPaymentMethodErrors = sanatizePaymentMethodErrors(allPaymentMethodErrors, formikProps.values?.paymentMethods ?? [], formikProps.initialValues?.paymentMethods ?? [])

    const nonePaymentMethodErrors = { ...errors }
    delete nonePaymentMethodErrors.paymentMethods
    delete nonePaymentMethodErrors.attendees

    const sanatizedErrors = { ...nonePaymentMethodErrors, ...sanatizedPaymentMethodErrors }

    const hasNonePaymentErrors =
        nonePaymentMethodErrors !== undefined && Object.keys(nonePaymentMethodErrors).length > 0
    const hasPaymentMethodErrors = sanatizedPaymentMethodErrors.some((error) => error !== undefined)
    const hasErrors = hasNonePaymentErrors || hasPaymentMethodErrors

    return { sanatizedErrors, hasErrors, hasPaymentMethodErrors, sanatizedPaymentMethodErrors }
}

/**
 * Erkennt den "E-Mail bereits vergeben"-Fehler der Registrierung.
 *
 * Das Backend antwortet bei bereits existierender E-Mail mit statusCode 400.
 * Geprüft wird zuerst strukturiert (ApolloError: graphQLErrors[].extensions
 * bzw. networkError.statusCode); das bisherige String-Matching auf der
 * Fehlermeldung bleibt als Fallback erhalten, da ältere Backend-Versionen den
 * Status nur in den Meldungstext serialisieren ("Unexpected error value:
 * { \"statusCode\": 400, ... }").
 */
export const isEmailNotUniqueError = (error: any, rawErrorMessage?: string): boolean => {
    // 1. Strukturiert: GraphQL-Fehler mit statusCode 400 in den extensions
    //    (Apollo Server legt geworfene Objekte je nach Version unter
    //    extensions.statusCode, extensions.exception bzw. extensions.response ab)
    const graphQLErrors: any[] = error?.graphQLErrors ?? []
    const hasStructured400 = graphQLErrors.some((gqlError) => {
        const extensions = gqlError?.extensions
        if (!extensions) return false
        const statusCode = extensions.statusCode ?? extensions.exception?.statusCode ?? extensions.response?.statusCode
        return statusCode === 400
    })
    if (hasStructured400) return true

    // 2. Strukturiert: Netzwerkfehler mit HTTP 400
    if (error?.networkError?.statusCode === 400) return true

    // 3. Fallback: bisheriges String-Matching (nicht entfernen!)
    const message = rawErrorMessage ?? ""
    if (message.includes("statusCode") && message.includes("400")) return true
    if (error instanceof Error && error.message.includes('statusCode": 400')) return true

    return false
}
